<template>
  <div
    class="property-notification-settings"
    data-testid="propertyNotificationSettings"
  >
    <bg-card>
      <bg-grid
        v-if="loading"
        data-testid="propertyNotificationSettings-skeleton"
      >
        <bg-grid-item v-for="i in 4" :key="i" :col="6">
          <bg-skeleton width="100%" :height="240" />
        </bg-grid-item>
      </bg-grid>

      <bg-grid v-else-if="recipients.length">
        <bg-grid-item
          v-for="recipient in recipients"
          :key="`${recipient.recipientType}-${recipient.recipientId}`"
          :col="6"
          class="property-notification-settings__grid-item"
        >
          <notification-setting-card
            :recipient="recipient"
            :types="notificationTypes"
            @edit="openEditModal"
          />
        </bg-grid-item>
      </bg-grid>

      <bg-text
        v-else
        size="body-2"
        class="property-notification-settings__empty"
      >
        Belum ada pengelola untuk properti ini.
      </bg-text>
    </bg-card>

    <notification-setting-edit-modal
      v-model="isEditModalOpen"
      :recipient="selectedRecipient"
      :types="notificationTypes"
      :saving="saving"
      @save="handleSave"
    />
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from '@vue/composition-api';
import { BgCard, BgGrid, BgGridItem, BgText, BgSkeleton } from 'bangul-vue';
import propertyDetailApi from '@admin_endpoints/property-detail';

import NotificationSettingCard from '@admin/pages/PropertyNotificationSettings/components/NotificationSettingCard';
import NotificationSettingEditModal from '@admin/pages/PropertyNotificationSettings/components/NotificationSettingEditModal';

const buildRecipients = data => {
  if (!data || Array.isArray(data)) return [];

  const recipients = [];

  if (data.owner) {
    recipients.push({
      ...data.owner,
      role: 'owner',
      recipientType: 'owner',
      recipientId: data.owner.user_id,
    });
  }

  (data.keepers || []).forEach(keeper => {
    recipients.push({
      ...keeper,
      role: 'keeper',
      recipientType: 'keeper',
      recipientId: keeper.user_id,
    });
  });

  return recipients;
};

export default {
  name: 'PropertyNotificationSettings',

  components: {
    BgCard,
    BgGrid,
    BgGridItem,
    BgText,
    BgSkeleton,
    NotificationSettingCard,
    NotificationSettingEditModal,
  },

  setup() {
    const { $route, $toast } = getCurrentInstance().proxy;
    const { propertyId } = $route.params;

    const loading = ref(true);
    const saving = ref(false);
    const recipients = ref([]);
    const notificationTypes = ref([]);
    const isEditModalOpen = ref(false);
    const selectedRecipient = ref({});

    const fetchData = async () => {
      loading.value = true;
      try {
        const [typeResponse, settingResponse] = await Promise.all([
          propertyDetailApi.getNotificationSettingType(),
          propertyDetailApi.getNotificationSetting(propertyId),
        ]);

        notificationTypes.value = typeResponse.data?.data || [];
        recipients.value = buildRecipients(settingResponse.data);
      } catch {
        notificationTypes.value = [];
        recipients.value = [];
      } finally {
        loading.value = false;
      }
    };

    const openEditModal = recipient => {
      selectedRecipient.value = recipient;
      isEditModalOpen.value = true;
    };

    const handleSave = async ({ recipient, enabledTypes }) => {
      saving.value = true;

      const updatedSettings = recipients.value.map(item =>
        item.recipientType === recipient.recipientType &&
        item.recipientId === recipient.recipientId
          ? { ...item, enabled_notification_types: enabledTypes }
          : item
      );

      const payload = {
        recipients: updatedSettings.map(item => ({
          recipient_type: item.recipientType,
          recipient_id: item.recipientId,
          enabled_notification_types: item.enabled_notification_types || [],
        })),
      };

      try {
        await propertyDetailApi.postNotificationSetting(propertyId, payload);

        recipients.value = updatedSettings;
        isEditModalOpen.value = false;

        $toast.show('Pengaturan notifikasi berhasil disimpan.');
      } catch {
        $toast.show(
          'Gagal menyimpan pengaturan notifikasi. Silakan coba lagi.',
          { variant: 'error' }
        );
      } finally {
        saving.value = false;
      }
    };

    onMounted(fetchData);

    return {
      loading,
      saving,
      recipients,
      notificationTypes,
      isEditModalOpen,
      selectedRecipient,
      openEditModal,
      handleSave,
    };
  },
};
</script>

<style lang="scss" scoped src="./PropertyNotificationSettings.scss" />
